import apiClient from "@/app/api/client";
import { endpoints } from "@/shared/api/endpoints";

export type BackendSalesReport = {
	totalSales: number;
	totalRevenue: number;
	avgSalePrice: number;
	byPaymentType: Record<string, number>;
	byBuilding: Array<{
		buildingId: string;
		buildingName: string;
		sales: number;
		revenue: number;
	}>;
	byManager: Array<{
		managerId: string;
		fullName: string;
		sales: number;
		revenue: number;
	}>;
	byMonth: Array<{
		month: string;
		sales: number;
		revenue: number;
	}>;
};

export type SalesReportParams = {
	dateFrom?: string;
	dateTo?: string;
	buildingId?: string;
	managerId?: string;
};

export type SalesReportData = {
	totalSales: number;
	totalAmount: number;
	totalPaid: number;
	totalDebt: number;
	averagePrice: number;
	cashSales: number;
	installmentSales: number;
	byBuilding: Array<{ buildingId: string; buildingName: string; sales: number; revenue: number }>;
	byManager: Array<{ managerId: string; fullName: string; sales: number; revenue: number }>;
	byMonth: Array<{ month: string; sales: number; revenue: number }>;
};

const monthLabels: Record<string, string> = {
	"01": "Yan",
	"02": "Fev",
	"03": "Mar",
	"04": "Apr",
	"05": "May",
	"06": "Iyun",
	"07": "Iyul",
	"08": "Avg",
	"09": "Sen",
	"10": "Okt",
	"11": "Noy",
	"12": "Dek",
};

function formatMonth(monthStr: string): string {
	const parts = monthStr.split("-");
	return monthLabels[parts[1] ?? ""] ?? monthStr;
}

export async function getSalesReport(params?: SalesReportParams): Promise<SalesReportData> {
	const response = await apiClient.get<{ success: boolean; data: BackendSalesReport }>(
		endpoints.reports.sales,
		{ params }
	);
	const data = response.data.data;
	const totalRevenue = data.totalRevenue ?? 0;
	const totalPaid = 0; // Not returned by backend / reports sales API
	const totalDebt = 0; // Not returned by backend / reports sales API

	const byPaymentType = data.byPaymentType ?? {};
	const cashSales = byPaymentType.NAQD ?? byPaymentType.naqd ?? 0;
	const installmentSales = byPaymentType.MUDDATLI ?? byPaymentType.muddatli ?? 0;

	return {
		totalSales: data.totalSales ?? 0,
		totalAmount: totalRevenue,
		totalPaid,
		totalDebt,
		averagePrice: data.avgSalePrice ?? 0,
		cashSales,
		installmentSales,
		byBuilding: (data.byBuilding ?? []).map((b) => ({
			buildingId: b.buildingId,
			buildingName: b.buildingName,
			sales: b.sales,
			revenue: b.revenue ?? 0,
		})),
		byManager: (data.byManager ?? []).map((m) => ({
			managerId: m.managerId,
			fullName: m.fullName,
			sales: m.sales,
			revenue: m.revenue ?? 0,
		})),
		byMonth: (data.byMonth ?? []).map((m) => ({
			month: formatMonth(m.month),
			sales: m.sales,
			revenue: m.revenue ?? 0,
		})),
	};
}
